import { addressSchema, addressWithCoordinatesSchema, billedBySchema, deliveryMethodSchema, earlierOwnerOwingSchema, externalSourceSchema, hiddenFromAgent, isSecretMask, ownerEmailForAgent, ownershipProfileTypeSchema, propertyTypeSchema, unitAdditionalDataSchema, unitAssessmentSchema, unitOwnerSchema, unitSaleSummarySchema, } from '@teo/types';
import { z } from 'zod';
import { agentNeverReads } from './agentNeverReads.js';
import { clearable } from './clearable.js';
import { displayId } from './common.js';
export const addPropertySchema = z.object({
    portfolioId: displayId,
    address: addressWithCoordinatesSchema.describe('Where the property is.'),
    propertyType: propertyTypeSchema.default('single_family'),
    totalUnits: z.number().default(1).describe('How many units the property has.'),
    floors: z.number().default(1).describe('How many floors the building has.'),
    yearBuilt: z.number().optional().describe('The year the building was built.'),
    squareFootage: z.number().optional().describe('The size of the whole property in square feet.'),
    description: z.string().optional().describe('Notes about the property.'),
});
export const updatePropertySchema = z.object({
    propertyId: displayId,
    displayOrder: z
        .number()
        .describe('The current position of the property in its portfolio. This route does not change it.'),
    propertyType: propertyTypeSchema.describe('The kind of building. It is always saved, so send the current kind to keep it.'),
    totalUnits: z.number().optional().describe('The new unit count. Omit to keep it.'),
    floors: z.number().optional().describe('The new floor count. Omit to keep it.'),
    yearBuilt: z.number().optional().describe('The new year built. Omit to keep it.'),
    squareFootage: z.number().optional().describe('The new size in square feet. Omit to keep it.'),
    description: z.string().optional().describe('The new notes. Omit to keep them.'),
});
export const deletePropertySchema = z.object({
    propertyId: displayId,
});
const unitIdentifier = z.string().optional().describe('The unit number or name, such as 1C.');
const floor = z.number().optional().describe('The floor the unit is on.');
const bedrooms = z.number().optional().describe('How many bedrooms the unit has.');
const bathrooms = z.number().optional().describe('How many bathrooms the unit has.');
const unitSquareFootage = z.number().optional().describe('The size of the unit in square feet.');
const unitDescription = z.string().optional().describe('Notes about the unit.');
const photos = z.array(z.string()).describe('The links to photos of the unit.');
const isUnitVerified = z.boolean().describe('Whether a person checked the unit record.');
const PLAIN_TEXT = /^[\x20-\x7e]*$/;
const ownerTaxIdToStore = z
    .string()
    .refine((value) => !isSecretMask(value), {
    message: 'That tax id is a mask, not a value. Send the full tax id to change it, or leave the field out to keep it.',
    abort: true,
})
    .refine((value) => PLAIN_TEXT.test(value), {
    message: 'That tax id has a character that is not plain text. Send only the tax id in plain text, for example 123-45-6789.',
});
const addedOwnerSchema = unitOwnerSchema.extend({
    taxId: ownerTaxIdToStore
        .optional()
        .describe('The owner tax id. Reads never show it to you. To set it, send the full value.'),
});
const ownerPatchShape = {
    names: clearable(z.string()).describe('The owner names, as one line.'),
    emails: ownerEmailForAgent(clearable(z.string()).describe('The owner email: one address. Every address after the first gets a copy of each invoice.')),
    invoiceEmailCc: hiddenFromAgent(clearable(z.string()).describe('More addresses that get a copy of each invoice, separated by commas.'), 'Every address in it gets a copy of each invoice, so only a person adds one.'),
    phones: clearable(z.string()).describe('The owner primary phone: one number.'),
    alternatePhone: clearable(z.string()).describe('The owner second phone: one number.'),
    physicalAddress: clearable(z.string()).describe('Where the owner lives, as one line.'),
    mailingAddress1: clearable(z.string()).describe('The first line of the owner mailing address.'),
    mailingAddress2: clearable(z.string()).describe('The second line of the owner mailing address.'),
    mailingCity: clearable(z.string()).describe('The city of the owner mailing address.'),
    mailingState: clearable(z.string()).describe('The state of the owner mailing address.'),
    mailingZip: clearable(z.string()).describe('The ZIP code of the owner mailing address.'),
    mailingCountry: clearable(z.string()).describe('The country of the owner mailing address.'),
    taxId: clearable(ownerTaxIdToStore).describe('The owner tax id. Reads never show it to you. To set it, send the full value.'),
    communicationMethod: clearable(deliveryMethodSchema).describe('How the owner gets invoices and notices.'),
};
export const unitOwnerPatchSchema = z.object(ownerPatchShape);
export const unitAdditionalDataPatchSchema = unitAdditionalDataSchema.extend({
    owner: unitOwnerPatchSchema
        .optional()
        .describe('The owner of the unit. Send only the owner fields you change. Leave a field out to keep it. Send null to clear it.'),
});
export const addUnitSchema = z.object({
    propertyId: displayId,
    unitIdentifier,
    floor,
    bedrooms,
    bathrooms,
    unitSquareFootage,
    photos: photos.default([]),
    unitDescription,
    isUnitVerified: hiddenFromAgent(isUnitVerified.default(false), 'It says that a person checked the unit record, so only a person sets it.'),
    additionalData: unitAdditionalDataSchema
        .extend({ owner: addedOwnerSchema.optional().describe('The owner of the unit.') })
        .optional()
        .describe('The owner, renter, property manager and other records of the unit.'),
});
export const addPropertyWithUnitsSchema = addPropertySchema
    .extend({
    createUnits: z
        .boolean()
        .default(false)
        .describe('Whether to add the first unit too. The unit fields below are for it.'),
    unitIdentifier,
    floor,
    bedrooms,
    bathrooms,
    unitSquareFootage,
    unitDescription,
    unitPhotos: photos.default([]),
    isUnitVerified: hiddenFromAgent(isUnitVerified.default(false), 'It says that a person checked the unit record, so only a person sets it.'),
})
    .refine((data) => {
    if (data.createUnits) {
        return data.unitIdentifier && data.unitIdentifier.trim().length > 0;
    }
    return true;
}, {
    message: 'Unit identifier is required for this property type',
    path: ['unitIdentifier'],
});
export const updateUnitSchema = z.object({
    id: displayId,
    propertyId: displayId,
    unitIdentifier: z.string().optional().describe('The new unit number or name. Omit to keep it.'),
    floor: z.number().optional().describe('The new floor. Omit to keep it.'),
    bedrooms: z.number().optional().describe('The new bedroom count. Omit to keep it.'),
    bathrooms: z.number().optional().describe('The new bathroom count. Omit to keep it.'),
    squareFootage: z.number().optional().describe('The new size in square feet. Omit to keep it.'),
    photos: z
        .array(z.string())
        .optional()
        .describe('All the photo links of the unit, which replace the old ones. Omit to keep them.'),
    description: z.string().optional().describe('The new notes. Omit to keep them.'),
    isUnitVerified: hiddenFromAgent(z.boolean().optional().describe('Whether a person checked the unit record. Omit to keep it.'), 'It says that a person checked the unit record, so only a person sets it.'),
    additionalData: unitAdditionalDataPatchSchema
        .optional()
        .describe('The owner, renter, property manager and other records. Omit a part to keep it.'),
    ownerTurnover: z
        .boolean()
        .optional()
        .describe('True only when the unit was sold and the owner in additionalData is a new person. Omit it to correct the current owner.'),
    expectedOwnerName: z
        .string()
        .nullable()
        .optional()
        .describe('The owner name you read for the unit before this change, or null if it had no owner. The change is refused if the owner changed since then.'),
});
export const getPendingOwnersSchema = z.object({
    propertyId: displayId,
});
export const getPendingOwnersOutputSchema = z.array(z.object({
    unitId: displayId,
    pendingOwnerName: z.string(),
}));
export const getUnitSaleSchema = z.object({
    propertyId: displayId,
    unitId: displayId,
});
export const getUnitSaleOutputSchema = unitSaleSummarySchema;
export const getEarlierOwnersOwingSchema = getUnitSaleSchema;
export const getEarlierOwnersOwingOutputSchema = z.array(earlierOwnerOwingSchema);
export const deleteUnitSchema = z.object({
    unitId: displayId,
});
export const updatePropertyOrderSchema = z.object({
    propertyId: displayId,
    displayOrder: z
        .number()
        .int()
        .positive()
        .describe('The new position in the portfolio list, where 1 is the top.'),
});
export const uploadAssessmentsSchema = z.object({
    propertyId: displayId,
    assessments: z.array(z.object({
        unitIdentifier: z.string(),
        assessment: unitAssessmentSchema.nullable(),
    })),
});
export const unitOwnerReadSchema = unitOwnerSchema.omit({ taxId: true }).extend({
    maskedTaxId: agentNeverReads(z
        .string()
        .optional()
        .describe('The owner tax id as bullets and its last 4 characters. A read for the agent leaves it out.')),
});
export const unitAdditionalDataReadSchema = unitAdditionalDataSchema.extend({
    owner: unitOwnerReadSchema.optional().describe('The owner of the unit.'),
});
export const unitOutputSchema = z.object({
    id: displayId,
    propertyId: displayId,
    unitIdentifier: z.string().nullable(),
    floor: z.number().nullable(),
    bedrooms: z.number().nullable(),
    bathrooms: z.number().nullable(),
    squareFootage: z.number().nullable(),
    photos: z.array(z.string()).nullable().default([]),
    description: z.string().nullable(),
    isUnitVerified: z.boolean().nullable(),
    additionalData: unitAdditionalDataReadSchema.nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const propertyOutputSchema = z.object({
    id: displayId,
    displayOrder: z.number(),
    address: addressWithCoordinatesSchema,
    propertyType: propertyTypeSchema,
    totalUnits: z
        .number()
        .nullable()
        .transform((val) => val ?? 1),
    floors: z
        .number()
        .nullable()
        .transform((val) => val ?? 1),
    yearBuilt: z.number().nullable(),
    squareFootage: z.number().nullable(),
    photos: z
        .array(z.string())
        .nullable()
        .transform((val) => val ?? []),
    description: z.string().nullable(),
    isAddressVerified: z.boolean().nullable(),
    externalPropertyId: z.string().nullable(),
    propertyCardData: z.any().nullable(),
    units: z.array(unitOutputSchema),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const addPropertyOutputSchema = z.object({
    id: displayId,
    address: addressWithCoordinatesSchema,
    propertyType: propertyTypeSchema,
    totalUnits: z.number().nullable(),
    floors: z.number().nullable(),
    yearBuilt: z.number().nullable(),
    squareFootage: z.number().nullable(),
    description: z.string().nullable(),
});
export const getUserPropertiesOutputSchema = z.array(z.object({
    property: propertyOutputSchema,
    portfolio: z.object({
        id: displayId,
        name: z.string(),
    }),
}));
export const getPropertyOutputSchema = propertyOutputSchema.extend({
    portfolio: z.object({
        user: z.object({
            id: displayId,
        }),
    }),
});
const propertySummaryUnitSchema = z.object({
    displayId: z.string(),
    unitIdentifier: z.string().optional(),
    floor: z.number().optional(),
    bedrooms: z.number().optional(),
    bathrooms: z.number().optional(),
    squareFootage: z.number().optional(),
    photos: z.array(z.string()).optional(),
    description: z.string().optional(),
    isUnitVerified: z.boolean().optional(),
    additionalData: unitAdditionalDataReadSchema.optional(),
    externalSource: externalSourceSchema.optional(),
    externalId: z.string().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    customerMapped: z.boolean().optional(),
    externalCustomerId: z.string().optional(),
    openingBalanceQboUrl: z.string().optional(),
    recurringInvoiceQboUrl: z.string().optional(),
});
export const getPropertySummaryOutputSchema = z.object({
    id: z.number(),
    displayId: z.string(),
    address: addressWithCoordinatesSchema,
    propertyType: propertyTypeSchema,
    totalUnits: z.number().optional(),
    floors: z.number().optional(),
    yearBuilt: z.number().optional(),
    squareFootage: z.number().optional(),
    photos: z.array(z.string()).optional(),
    description: z.string().optional(),
    isAddressVerified: z.boolean().optional(),
    externalPropertyId: z.string().optional(),
    propertyCardData: z.unknown().optional(),
    qboLastRefreshedAt: z.date().optional(),
    displayOrder: z.number(),
    createdAt: z.date(),
    updatedAt: z.date(),
    ownershipName: z.string().optional(),
    ownershipId: z.string().optional(),
    ownershipProfileType: ownershipProfileTypeSchema.optional(),
    billedBy: billedBySchema.optional(),
    units: z.array(propertySummaryUnitSchema),
    arLive: z.boolean(),
    summary: z.object({
        unitsActual: z.number(),
        unitsPlanned: z.number().optional(),
        totalBedrooms: z.number(),
        totalBathrooms: z.number(),
        unitSquareFootage: z.number(),
        averageBedroomsPerUnit: z.number(),
    }),
});
export const updatePropertyOrderOutputSchema = z.object({
    propertyId: displayId,
    displayOrder: z.number().int().positive(),
});
export const rentRollRowSchema = z.object({
    unitNumber: z.string(),
    leaseType: z.string(),
    leaseStatus: z.string(),
    leaseStartDate: z.string(),
    leaseEndDate: z.string(),
    moveInDate: z.string(),
    moveOutDate: z.string(),
    monthlyRent: z.string(),
    securityDeposit: z.string(),
    petsAllowed: z.string(),
    petsDeposit: z.string(),
    petsFee: z.string(),
    storageFee: z.string(),
    laundryFee: z.string(),
    parkingFee: z.string(),
    otherFee: z.string(),
    renewalOptions: z.string(),
    leaseNotes: z.string(),
    renterName: z.string(),
    renterEmail: z.string(),
    renterPhone: z.string(),
    renterMailingStreet: z.string(),
    renterMailingCity: z.string(),
    renterMailingState: z.string(),
    renterMailingZip: z.string(),
    renterMailingCountry: z.string(),
    emergencyContactName: z.string(),
    emergencyContactEmail: z.string(),
    emergencyContactPhone: z.string(),
    insuranceCompany: z.string(),
    primaryTenant: z.string(),
    tenantStartDate: z.string(),
    tenantEndDate: z.string(),
});
export const uploadRentRollSchema = z.object({
    propertyId: displayId,
    rows: z.array(rentRollRowSchema),
    deleteRenterIds: z.array(z.string()).default([]),
});
export const uploadRentRollOutputSchema = z.object({
    createdUnits: z.number(),
    createdLeases: z.number(),
    updatedLeases: z.number(),
    createdRenters: z.number(),
    updatedRenters: z.number(),
    deletedRenters: z.number(),
    writtenSchedules: z.number(),
});
const rentRollLeaseSchema = z.object({
    leaseType: z.string(),
    status: z.string(),
    startDate: z.string(),
    endDate: z.string().nullable(),
    moveInDate: z.string().nullable(),
    moveOutDate: z.string().nullable(),
    leaseAmount: z.number(),
    securityDeposit: z.number().nullable(),
    petsAllowed: z.boolean(),
    petsDeposit: z.number().nullable(),
    petsFee: z.number().nullable(),
    storageFee: z.number().nullable(),
    renewalOptions: z.string().nullable(),
    notes: z.string().nullable(),
});
const rentRollRenterSchema = z.object({
    id: z.string(),
    name: z.string(),
    emails: z.array(z.object({ label: z.string(), value: z.string() })),
    phones: z.array(z.object({ label: z.string(), value: z.string() })),
    mailingAddress: z
        .object({
        street: z.string(),
        city: z.string(),
        state: z.string(),
        zipCode: z.string(),
        country: z.string(),
    })
        .nullable(),
    emergencyContact: z
        .object({
        name: z.string().optional(),
        email: z.string().optional(),
        phone: z.string().optional(),
    })
        .nullable(),
    insuranceCompany: z.string().nullable(),
    isPrimary: z.boolean(),
    tenantStartDate: z.string().nullable(),
    tenantEndDate: z.string().nullable(),
});
export const getRentRollSchema = z.object({ propertyId: displayId });
export const getRentRollOutputSchema = z.object({
    units: z.array(z.object({
        unitNumber: z.string().nullable(),
        lease: rentRollLeaseSchema.nullable(),
        renters: z.array(rentRollRenterSchema),
        leases: z.array(rentRollLeaseSchema.extend({
            isCurrent: z.boolean(),
            renters: z.array(rentRollRenterSchema),
        })),
    })),
});
export const getLeaseOccupancyOutputSchema = z.object({
    units: z.array(z.object({
        unitDisplayId: displayId,
        unitIdentifier: z.string().optional(),
        property: z.object({ displayId: displayId, address: addressSchema }),
        ownership: z.object({ displayId: displayId, name: z.string() }),
        renter: unitAdditionalDataSchema.shape.renter.unwrap().nullable(),
    })),
});
//# sourceMappingURL=properties.js.map